import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type {
  LogEntryPayload,
  LogLevel,
  LogOptions,
} from '../interfaces/log-entry';
import { LOG_SERVICE_FE_CONFIG } from './log-service-fe.config';
import { createTraceId } from '../../http/trace-id';

export interface LogResponse {
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LogServiceFe {
  private readonly http = inject(HttpClient);
  private readonly config = inject(LOG_SERVICE_FE_CONFIG, { optional: true });

  log(level: LogLevel, message: string, options: LogOptions) {
    const serviceName = options.serviceName ?? this.config?.serviceName;
    if (!serviceName) {
      throw new Error('LogServiceFe: missing serviceName configuration.');
    }

    const component = options.component || this.config?.defaultComponent;
    if (!component) {
      throw new Error('LogServiceFe: missing component.');
    }

    const traceId = options.traceId ?? this.generateTraceId();
    const payload: LogEntryPayload = {
      service_name: serviceName,
      level,
      message,
      trace_id: traceId,
      component,
      metadata: options.metadata ?? {},
    };

    return this.http.post<LogResponse>(this.getEndpoint(), payload);
  }

  info(message: string, options: LogOptions) {
    return this.log('INFO', message, options);
  }

  warn(message: string, options: LogOptions) {
    return this.log('WARN', message, options);
  }

  error(message: string, options: LogOptions) {
    return this.log('ERROR', message, options);
  }

  debug(message: string, options: LogOptions) {
    return this.log('DEBUG', message, options);
  }

  private getEndpoint(): string {
    return this.config?.endpoint ?? '/ingest';
  }

  private generateTraceId(): string {
    return createTraceId();
  }
}
